import React from 'react';
import { Modal } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useMaintenancePageController } from './useMaintenancePageController';
import classes from './maintenance-page.module.scss';

export const MaintenancePage: React.FC = () => {
  const {
    tenants,
    isLoading,
    error,
    pendingModeChange,
    isSubmitting,
    isSelectMode,
    selectedTenantIds,
    selectableTenants,
    allTenantsInMaintenance,
    requestModeChange,
    closeConfirmation,
    toggleSelectMode,
    toggleTenantSelection,
    selectAllTenants,
    requestSelectedMaintenance,
    requestCancelAllMaintenance,
    confirmModeChange,
  } = useMaintenancePageController();
  const enabling = pendingModeChange?.maintenanceMode === true;
  const pendingCount = pendingModeChange?.tenants.length ?? 0;

  return (
    <main className={classes.container} dir="rtl">
      <section className={classes.card}>
        <header className={classes.header}>
          <FontAwesomeIcon icon={faScrewdriverWrench as IconProp} className={classes.icon} aria-hidden="true" />
          <div>
            <h1>תחזוקה</h1>
            <p>ניהול מצב תחזוקה לפי חברה. הפעולה מעבירה משתמשים אנושיים למסך התחזוקה בלבד.</p>
          </div>
        </header>
        {error && <p className={classes.error}>{error}</p>}
        {isLoading ? <p className={classes.empty}>טוען חברות…</p> : (
          <>
            <div className={classes.toolbar}>
              {allTenantsInMaintenance ? (
                <button type="button" className={classes.disableButton} onClick={requestCancelAllMaintenance} data-testid="maintenance-cancel-all">
                  בטל תחזוקה לכולם
                </button>
              ) : (
                <>
                  <button type="button" className="btn-bordered" onClick={toggleSelectMode} data-testid="maintenance-toggle-select">
                    {isSelectMode ? 'ביטול בחירה' : 'בחירה מרובה'}
                  </button>
                  {isSelectMode && (
                    <>
                      <button type="button" className="btn-bordered" onClick={selectAllTenants} data-testid="maintenance-select-all">
                        בחר הכל ({selectableTenants.length})
                      </button>
                      <button
                        type="button"
                        className={classes.enableButton}
                        onClick={requestSelectedMaintenance}
                        disabled={selectedTenantIds.length === 0}
                        data-testid="maintenance-enable-selected"
                      >
                        הפעל תחזוקה לנבחרים ({selectedTenantIds.length})
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
            <div className={classes.list}>
              {tenants.map((tenant) => (
                <article key={tenant.id} className={classes.tenant}>
                  <div className={classes.tenantDetails}>
                    {isSelectMode && !tenant.maintenanceMode && (
                      <input
                        type="checkbox"
                        className={classes.selection}
                        checked={selectedTenantIds.includes(tenant.id)}
                        onChange={() => toggleTenantSelection(tenant.id)}
                        aria-label={`בחירת ${tenant.companyName}`}
                        data-testid={`maintenance-select-${tenant.id}`}
                      />
                    )}
                    <div>
                      <h2>{tenant.companyName}</h2>
                      <p>{tenant.maintenanceMode ? 'מצב תחזוקה פעיל' : 'פעילות רגילה'}</p>
                    </div>
                  </div>
                  {!isSelectMode && (
                    <button
                      type="button"
                      className={tenant.maintenanceMode ? classes.disableButton : classes.enableButton}
                      onClick={() => requestModeChange(tenant)}
                      data-testid={`maintenance-toggle-${tenant.id}`}
                    >
                      {tenant.maintenanceMode ? 'הפסק תחזוקה' : 'הפעל תחזוקה'}
                    </button>
                  )}
                </article>
              ))}
            </div>
          </>
        )}
      </section>
      <Modal
        opened={Boolean(pendingModeChange)}
        onClose={closeConfirmation}
        centered
        title={enabling ? 'הפעלת מצב תחזוקה' : 'הפסקת מצב תחזוקה'}
        closeButtonProps={{ 'data-testid': 'maintenance-confirm-close' } as Record<string, string>}
      >
        <div className={classes.confirmation} dir="rtl">
          <p>
            {enabling
              ? `להעביר ${pendingCount === 1 ? `את משתמשי ${pendingModeChange?.tenants[0]?.companyName ?? ''}` : `${pendingCount} חברות`} למסך התחזוקה?`
              : `להחזיר ${pendingCount === 1 ? `את משתמשי ${pendingModeChange?.tenants[0]?.companyName ?? ''}` : `${pendingCount} חברות`} לפעילות רגילה?`}
          </p>
          <div className={classes.confirmationActions}>
            <button type="button" className="btn-bordered" onClick={closeConfirmation} disabled={isSubmitting} data-testid="maintenance-confirm-cancel">ביטול</button>
            <button type="button" className={enabling ? classes.enableButton : classes.disableButton} onClick={() => void confirmModeChange()} disabled={isSubmitting} data-testid="maintenance-confirm-submit">
              {isSubmitting ? 'מעדכן…' : enabling ? 'הפעל תחזוקה' : 'הפסק תחזוקה'}
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
};
